import { useState, memo, useCallback } from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Wallet, 
  Receipt, 
  ArrowLeftRight, 
  Settings,
  Bell,
  Menu,
  X,
  Calendar,
  FileText,
  LogOut,
  User,
  Store,
  FileCheck,
  MoreHorizontal,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import NotificationsSheet from '@/components/NotificationsSheet';
import NotificationDropdown from '@/components/NotificationDropdown';
import { useAuth } from '@/context/AuthContext';

const navItems = [
  { path: '/', label: 'Özet', icon: LayoutDashboard },
  { path: '/borclar', label: 'Borçlar', icon: Wallet },
  { path: '/musteri-cekleri', label: 'Müşteri Çekleri', icon: FileCheck },
  { path: '/giderler', label: 'Giderler', icon: Receipt },
  { path: '/pazaryeri', label: 'Pazaryeri', icon: Store },
  { path: '/islemler', label: 'İşlemler', icon: ArrowLeftRight },
  { path: '/takvim', label: 'Takvim', icon: Calendar },
  { path: '/raporlar', label: 'Raporlar', icon: FileText },
  { path: '/ayarlar', label: 'Ayarlar', icon: Settings },
];

// Mobile bottom nav: show these paths as primary tabs, rest in the "Daha" drawer
const MOBILE_PRIMARY_PATHS = ['/', '/borclar', '/musteri-cekleri', '/giderler'];

// Memoized NavItem component
const NavItem = memo(({ item, mobile = false, isActive, onMobileClick }) => {
  const Icon = item.icon;
  
  return (
    <NavLink
      to={item.path}
      onClick={mobile ? onMobileClick : undefined}
      className={cn(
        "flex items-center gap-3 px-4 py-3 rounded-lg transition-colors duration-200 touch-target",
        isActive 
          ? "bg-primary/10 text-primary" 
          : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
      )}
      data-testid={`nav-${item.path.replace('/', '') || 'dashboard'}`}
    >
      <Icon className="w-5 h-5" />
      <span className="font-medium">{item.label}</span>
      {isActive && (
        <div className="ml-auto w-1.5 h-1.5 rounded-full bg-primary" />
      )}
    </NavLink>
  );
});

// ============== Mobile Bottom Nav ==============
const MobileNavItem = memo(({ item, onClick }) => {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.path}
      onClick={onClick}
      className={({ isActive }) => cn(
        "relative flex flex-col items-center justify-center flex-1 min-w-0 py-2 px-1 rounded-xl transition-all duration-200",
        isActive
          ? "text-sky-600 bg-sky-50"
          : "text-gray-600 active:bg-gray-50",
      )}
      data-testid={`mobile-nav-${item.path.replace('/', '') || 'dashboard'}`}
    >
      {({ isActive }) => (
        <>
          <div className={cn(
            "w-6 h-6 flex items-center justify-center transition-transform",
            isActive && "scale-110",
          )}>
            <Icon className="w-5 h-5" strokeWidth={isActive ? 2.5 : 2} />
          </div>
          <span className={cn(
            "text-[10px] font-medium mt-1 truncate max-w-full leading-tight",
            isActive && "font-semibold",
          )}>
            {item.label}
          </span>
          {isActive && (
            <div className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-sky-600"></div>
          )}
        </>
      )}
    </NavLink>
  );
});

const MobileBottomNav = () => {
  const [moreOpen, setMoreOpen] = useState(false);
  const primaryItems = navItems.filter(i => MOBILE_PRIMARY_PATHS.includes(i.path));
  const moreItems = navItems.filter(i => !MOBILE_PRIMARY_PATHS.includes(i.path));
  const location = useLocation();
  const isMoreActive = moreItems.some(i => location.pathname.startsWith(i.path) && i.path !== '/');

  return (
    <>
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40" data-testid="mobile-bottom-nav">
        <div className="absolute inset-0 bg-white/80 backdrop-blur-lg border-t border-gray-200/50"></div>
        <div className="relative flex items-center justify-around px-1 pt-2 pb-safe gap-1">
          {primaryItems.map(item => (
            <MobileNavItem key={item.path} item={item} />
          ))}
          <button
            type="button"
            onClick={() => setMoreOpen(true)}
            className={cn(
              "relative flex flex-col items-center justify-center flex-1 min-w-0 py-2 px-1 rounded-xl transition-all duration-200",
              isMoreActive ? "text-sky-600 bg-sky-50" : "text-gray-600 active:bg-gray-50",
            )}
            data-testid="mobile-nav-more"
          >
            <div className={cn(
              "w-6 h-6 flex items-center justify-center transition-transform",
              isMoreActive && "scale-110",
            )}>
              <MoreHorizontal className="w-5 h-5" strokeWidth={isMoreActive ? 2.5 : 2} />
            </div>
            <span className={cn(
              "text-[10px] font-medium mt-1 leading-tight",
              isMoreActive && "font-semibold",
            )}>Daha</span>
            {isMoreActive && (
              <div className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-sky-600"></div>
            )}
          </button>
        </div>
      </nav>

      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl pb-safe" data-testid="mobile-more-sheet">
          <div className="pt-2 pb-4">
            <h3 className="text-lg font-semibold mb-3">Diğer Bölümler</h3>
            <div className="grid grid-cols-3 gap-2">
              {moreItems.map(item => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={() => setMoreOpen(false)}
                    className={({ isActive }) => cn(
                      "flex flex-col items-center justify-center gap-1.5 p-3 rounded-xl border transition-all",
                      isActive
                        ? "border-sky-500 bg-sky-50 text-sky-700"
                        : "border-gray-200 bg-white active:bg-gray-50 text-gray-700",
                    )}
                    data-testid={`mobile-more-nav-${item.path.replace('/', '')}`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="text-xs font-medium text-center leading-tight">{item.label}</span>
                  </NavLink>
                );
              })}
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

const Layout = () => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = useCallback(() => {
    logout();
  }, [logout]);

  const handleMobileMenuClose = useCallback(() => {
    setMobileMenuOpen(false);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex fixed left-0 top-0 bottom-0 w-64 flex-col bg-card border-r border-border z-40">
        {/* Logo */}
        <div className="p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <img 
              src="/logo.png" 
              alt="PayFlow Logo" 
              className="w-10 h-10 rounded-lg object-contain"
            />
            <h1 className="text-2xl font-bold font-heading text-foreground">
              PayFlow
            </h1>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => (
            <NavItem 
              key={item.path} 
              item={item} 
              isActive={location.pathname === item.path}
            />
          ))}
        </nav>

      </aside>

      {/* Mobile Header - Centered logo */}
      <header className="md:hidden fixed top-0 left-0 right-0 bg-white z-40 safe-top shadow-sm">
        <div className="flex items-center justify-center px-4 py-3 relative">
          {/* Centered Logo */}
          <div className="flex items-center gap-2">
            <img 
              src="/logo.png" 
              alt="PayFlow Logo" 
              className="w-10 h-10 rounded-xl object-contain"
            />
            <h1 className="text-xl font-bold font-heading text-gray-900">
              PayFlow
            </h1>
          </div>
          
          {/* Right side - Notifications & User Menu */}
          <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
            <NotificationDropdown />
            
            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="rounded-full">
                  <div className="w-8 h-8 rounded-full bg-sky-100 flex items-center justify-center">
                    <User className="w-4 h-4 text-sky-600" />
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user?.name || 'Kullanıcı'}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => window.location.href = '/ayarlar'}>
                  <Settings className="w-4 h-4 mr-2" />
                  Ayarlar
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                  <LogOut className="w-4 h-4 mr-2" />
                  Çıkış Yap
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="md:ml-64 pt-20 md:pt-0 pb-24 md:pb-6">
        <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto">
          <Outlet />
        </div>
      </main>

      {/* Mobile Bottom Navigation - Data-driven with "Daha" (More) drawer */}
      <MobileBottomNav />

      {/* Notifications Dropdown - Desktop */}
      <div className="hidden md:block fixed top-4 right-24 z-50">
        <NotificationDropdown />
      </div>

      {/* Notifications Sheet */}
      <NotificationsSheet open={notificationsOpen} onOpenChange={setNotificationsOpen} />
    </div>
  );
};

export default memo(Layout);
